import { applicationTabValidation, applicationPropertyTabValidation, unformatPhoneNumber } from "@/types/application";
import { formatDateForAPI } from "@/features/applicant/utils/addressHelpers";
import { normalizeDateFromAPI } from "../utils/applicationFormatters";
import {
  validateAboutYourselfTab,
  isOutsideUSWithoutAlternate,
  type AboutYourselfFormData,
} from "../utils/aboutYourselfValidator";
import {
  getPropertyCoResidentInputs,
  resolvePropertyOtherIndividualsFlag,
  validatePropertyTab,
  resolvePropertyAgentFields,
  mapCoResidentToApi,
  type PropertyTabFormData,
  type PropertyCoResidentInput,
  type PropertyApplicationContext,
} from "../utils/propertyTabValidator";
import {
  validateIncomeEmploymentTab,
} from "../utils/incomeEmploymentValidator";
import { buildIncomeEmploymentEffectiveData } from "../components/tabs/income-employment/employmentFormModel";
import type {
  ApplicationDetailData,
  ApplicationLayerData,
} from "../types/application";
import type {
  ApplicationDetailFormDelta,
  TabSaveBuildResult,
} from "../types/pages";
import type { TabSaveBuildContext } from "../types";
import type {
  AddressLike,
  AlternateAddressLike,
  PreviousAddressRow,
} from "../types/utils";
import {
  buildAlternateAddressForApi,
  extractDeletedAddressIds,
  extractDeletedJobIds,
  hasReimbursementRentalApplicationStatus,
  isApiOne,
  isApprovedWithLeaseEmailStatus,
  isBeforeToday,
  isPresent,
  normalizeLeaseDateForCompare,
  normalizePhoneForAngularPayload,
  normalizeRentForAngularPayload,
  normalizeRentForCompare,
  normalizeTextForCompare,
  orderPropertyPayloadLikeAngular,
  coerceApiNumericId,
  resolveAboutYourselfPersonalField,
  resolveAddressRaId,
  resolveStreetTypeForApi,
  toApiCurrentlyLivingFlag,
} from "./applicationDetailHelpers";

function buildAboutYourselfPayload(
  formData: ApplicationDetailFormDelta,
  applicationData: ApplicationLayerData,
): TabSaveBuildResult {
  const effective: AboutYourselfFormData = {
    first_name: resolveAboutYourselfPersonalField(formData, applicationData, "first_name"),
    middle_name: resolveAboutYourselfPersonalField(formData, applicationData, "middle_name"),
    last_name: resolveAboutYourselfPersonalField(formData, applicationData, "last_name"),
    suffix: resolveAboutYourselfPersonalField(formData, applicationData, "suffix"),
    mobile_no: resolveAboutYourselfPersonalField(formData, applicationData, "mobile_no"),
    shared_phone_no: (() => {
      const raw =
        formData.shared_phone_no ?? applicationData?.user_data?.shared_phone_no;
      return raw === true || raw === "1" ? "1" : "0";
    })(),
    email: resolveAboutYourselfPersonalField(formData, applicationData, "email"),
    communication_preference:
      formData.communication_preference ?? applicationData?.communication_preference,
    birthdate: formData.birthdate ?? applicationData?.birthdate,
    security_number: formData.security_number ?? applicationData?.security_number,
    non_us_address: (() => {
      const raw = formData.non_us_address ?? applicationData?.non_us_address;
      return raw === true || raw === "1" ? "1" : "";
    })(),
    current_address: formData.current_address ?? applicationData?.current_address,
    previous_address:
      formData.previous_address ?? applicationData?.previous_address ?? [],
    alternate_address:
      formData.alternate_address ?? applicationData?.alternate_address ?? [],
  };

  if (isOutsideUSWithoutAlternate(effective)) {
    return { ok: false, kind: "address_error" };
  }

  const result = validateAboutYourselfTab(effective);
  if (!result.isValid) {
    return {
      ok: false,
      kind: "validation",
      errors: result.errors,
      tab: "aboutyourselfinfo",
    };
  }

  const requestData: Record<string, unknown> = {
    first_name: effective.first_name || "",
    middle_name: effective.middle_name || "",
    last_name: effective.last_name || "",
    suffix: effective.suffix || "",
    email: effective.email || "",
    birthdate: effective.birthdate || "",
    communication_preference: effective.communication_preference || "",
    mobile_no: unformatPhoneNumber(String(effective.mobile_no || "")),
    shared_phone_no:
      effective.shared_phone_no === true || effective.shared_phone_no === "1"
        ? "1"
        : "0",
    non_us_address: (() => {
      const nu = effective.non_us_address as unknown;
      return nu === true || nu === "1" || nu === 1 ? "1" : "";
    })(),
    current_address: (() => {
      const addr = effective.current_address as AddressLike | undefined;
      if (!addr) return addr;
      const ad = addr.address_data || {};
      const baseline = applicationData?.current_address as
        | Record<string, unknown>
        | undefined;
      return {
        ...addr,
        ra_id: resolveAddressRaId(addr.ra_id, baseline?.ra_id),
        outside_us: addr.outside_us ?? false,
        street_type: resolveStreetTypeForApi(addr, baseline),
        apartment: addr.apartment === "" ? null : addr.apartment,
        address_data: {
          ...ad,
          currently_living: toApiCurrentlyLivingFlag(ad.currently_living),
          moveOut_date: ad.moveOut_date === "" ? null : ad.moveOut_date,
        },
      };
    })(),
    previous_address: (effective.previous_address ?? []).map(
      (a: PreviousAddressRow, index: number) => {
        const ad = a?.address_data || {};
        const baseline = (
          applicationData?.previous_address as unknown[] | undefined
        )?.[index] as Record<string, unknown> | undefined;
        return {
          ...a,
          ra_id: resolveAddressRaId(a?.ra_id, baseline?.ra_id),
          outside_us: a?.outside_us ?? false,
          street_type: resolveStreetTypeForApi(a, baseline),
          apartment: a?.apartment === "" ? null : a?.apartment,
          address_data: {
            ...ad,
            currently_living: toApiCurrentlyLivingFlag(ad.currently_living),
            moveOut_date: ad.moveOut_date === "" ? null : ad.moveOut_date,
          },
        };
      },
    ),
    alternate_address: (effective.alternate_address ?? []).map(
      (a: AlternateAddressLike, index: number) => {
        const baseline = (
          applicationData?.alternate_address as unknown[] | undefined
        )?.[index] as Record<string, unknown> | undefined;
        return buildAlternateAddressForApi(a, baseline);
      },
    ),
    is_mobile_no: applicationData?.is_mobile_no ?? "1",
    update_via_sms: applicationData?.update_via_sms ?? "0",
    enter_date: {},
  };

  const ssnRaw = String(effective.security_number || "").trim();
  const ssnDigits = ssnRaw.replace(/\D/g, "");
  if (ssnDigits.length === 9) {
    requestData.security_number = ssnDigits;
  } else if (ssnRaw) {
    requestData.security_number = ssnRaw;
  }

  // Angular getRequestBody — attach `deleted` only when saved rows were removed.
  const deletedAddressIds = extractDeletedAddressIds(formData.deleted);
  if (
    deletedAddressIds.previous_address.length > 0 ||
    deletedAddressIds.alternate_address.length > 0
  ) {
    requestData.deleted = {
      ...(deletedAddressIds.previous_address.length > 0
        ? { previous_address: deletedAddressIds.previous_address }
        : {}),
      ...(deletedAddressIds.alternate_address.length > 0
        ? { alternate_address: deletedAddressIds.alternate_address }
        : {}),
    };
  }

  Object.assign(requestData, applicationTabValidation("about"));

  let shouldPromptLeaseDetailsChanged = false;
  if (isApprovedWithLeaseEmailStatus(applicationData)) {
    const oldUserData = applicationData?.user_data ?? {};
    const firstNameChanged =
      normalizeTextForCompare(effective.first_name) !==
      normalizeTextForCompare(
        oldUserData.first_name ?? applicationData?.first_name,
      );
    const lastNameChanged =
      normalizeTextForCompare(effective.last_name) !==
      normalizeTextForCompare(
        oldUserData.last_name ?? applicationData?.last_name,
      );
    shouldPromptLeaseDetailsChanged = firstNameChanged || lastNameChanged;
  }

  return {
    ok: true,
    requestData,
    shouldPromptLeaseStartDateAdjust: false,
    shouldPromptLeaseDetailsChanged,
  };
}

function buildPropertyPayload(
  formData: ApplicationDetailFormDelta,
  applicationData: ApplicationLayerData,
  applicationAllData: ApplicationDetailData,
): TabSaveBuildResult {
  const propertyEffective: PropertyTabFormData = {
    has_applied:
      (formData.has_applied as boolean | null | undefined) ?? null,
    property_name:
      (formData.property_name as string | undefined) ??
      applicationAllData?.property?.property_name ??
      applicationAllData?.property_name ??
      "",
    property_state:
      (formData.property_state as string | undefined) ??
      applicationAllData?.property?.property_state ??
      applicationAllData?.property_state ??
      "",
    rent_amount: Object.prototype.hasOwnProperty.call(formData, "rent_amount")
      ? (formData.rent_amount ?? "")
      : applicationAllData?.rent_amount,
    lease_start_date: Object.prototype.hasOwnProperty.call(
      formData,
      "lease_start_date",
    )
      ? (formData.lease_start_date ?? "")
      : applicationAllData?.lease_start_date,
    lease_duration:
      (formData.lease_duration as string | number | undefined) ??
      applicationAllData?.lease_duration,
    unit_number:
      (formData.unit_number as string | undefined) ??
      applicationAllData?.unit_number ??
      undefined,
    rent_lease_reason: formData.rent_lease_reason as string | undefined,
    invite_code: formData.invite_code as string | undefined,
    other_individuals: resolvePropertyOtherIndividualsFlag(
      formData.other_individuals,
      applicationData,
      applicationAllData,
    ),
    co_residents: (Object.prototype.hasOwnProperty.call(formData, "co_residents")
      ? formData.co_residents
      : getPropertyCoResidentInputs(applicationAllData)) as PropertyCoResidentInput[],
    ...resolvePropertyAgentFields(
      {
        agent_id: formData.agent_id as string | undefined,
        agent_name: formData.agent_name as string | undefined,
        agent_email: formData.agent_email as string | undefined,
        agent_phone: formData.agent_phone as string | undefined,
      },
      applicationAllData,
    ),
  };

  const originalRent = applicationAllData?.rent_amount ?? "";
  const originalLease = applicationAllData?.lease_start_date ?? "";
  const hasRentChanged =
    formData.rent_amount !== undefined &&
    normalizeRentForCompare(formData.rent_amount) !==
      normalizeRentForCompare(originalRent);
  const hasLeaseChanged =
    formData.lease_start_date !== undefined &&
    normalizeLeaseDateForCompare(formData.lease_start_date) !==
      normalizeLeaseDateForCompare(originalLease);
  const propertyContext: PropertyApplicationContext = {
    primaryEmail: applicationData?.user_data?.email ?? applicationData?.email,
    primaryMobile:
      applicationData?.user_data?.mobile_no ?? applicationData?.mobile_no,
    hasRentOrLeaseChanged: hasRentChanged || hasLeaseChanged,
    coResidentInviteRelaxed: Boolean(applicationAllData?.invite_code),
  };

  const propResult = validatePropertyTab(propertyEffective, propertyContext);
  if (!propResult.isValid) {
    return {
      ok: false,
      kind: "validation",
      errors: propResult.errors,
      tab: "propertyinfo",
    };
  }

  const requestData: Record<string, unknown> = {
    applied_property: propertyEffective.has_applied === false ? "0" : "1",
    other_individuals:
      propertyEffective.other_individuals === true ||
      propertyEffective.other_individuals === "1"
        ? "1"
        : "0",
  };

  if (formData.fk_property_id !== undefined) {
    const propertyId = coerceApiNumericId(formData.fk_property_id);
    if (propertyId !== undefined) requestData.fk_property_id = propertyId;
  } else if (applicationAllData?.fk_property_id !== undefined) {
    const propertyId = coerceApiNumericId(applicationAllData.fk_property_id);
    if (propertyId !== undefined) requestData.fk_property_id = propertyId;
  } else if (formData.fk_temp_property_id !== undefined) {
    const tempPropertyId = coerceApiNumericId(formData.fk_temp_property_id);
    if (tempPropertyId !== undefined) {
      requestData.fk_temp_property_id = tempPropertyId;
    }
  } else if (applicationAllData?.fk_temp_property_id !== undefined) {
    const tempPropertyId = coerceApiNumericId(
      applicationAllData.fk_temp_property_id,
    );
    if (tempPropertyId !== undefined) {
      requestData.fk_temp_property_id = tempPropertyId;
    }
  }
  if (
    formData.fk_agent_id !== undefined &&
    formData.fk_agent_id !== null &&
    formData.other_agent === undefined
  ) {
    const agentId = coerceApiNumericId(formData.fk_agent_id);
    if (agentId !== undefined) requestData.fk_agent_id = agentId;
  } else if (
    applicationAllData?.fk_agent_id !== undefined &&
    applicationAllData?.fk_agent_id !== null &&
    formData.other_agent === undefined
  ) {
    const agentId = coerceApiNumericId(applicationAllData.fk_agent_id);
    if (agentId !== undefined) requestData.fk_agent_id = agentId;
  }
  if (isPresent(propertyEffective.rent_amount)) {
    requestData.rent_amount = normalizeRentForAngularPayload(
      propertyEffective.rent_amount,
    );
  }
  if (propertyEffective.lease_start_date) {
    const leaseStartDate = String(propertyEffective.lease_start_date);
    requestData.lease_start_date =
      normalizeDateFromAPI(leaseStartDate) || formatDateForAPI(leaseStartDate);
  }
  if (propertyEffective.lease_duration !== undefined) {
    requestData.lease_duration = String(propertyEffective.lease_duration).replace(
      /\s*months?\s*/i,
      "",
    );
  }
  if (isPresent(propertyEffective.unit_number)) {
    requestData.unit_number = propertyEffective.unit_number;
  }
  if (propertyEffective.rent_lease_reason) {
    requestData.rent_lease_reason = propertyEffective.rent_lease_reason;
  }
  if (propertyEffective.invite_code) {
    requestData.invite_code = propertyEffective.invite_code;
  }
  if (
    propertyEffective.other_individuals === true ||
    propertyEffective.other_individuals === "1"
  ) {
    requestData.co_resident = (propertyEffective.co_residents ?? []).map(
      mapCoResidentToApi,
    );
  }
  if (formData.other_agent !== undefined) {
    const otherAgent = formData.other_agent as Record<string, unknown>;
    requestData.other_agent = {
      agent_name: String(otherAgent.agent_name ?? ""),
      agent_email: String(otherAgent.agent_email ?? ""),
      agent_mobile_no: normalizePhoneForAngularPayload(
        otherAgent.agent_mobile_no,
      ),
    };
  }

  Object.assign(requestData, applicationPropertyTabValidation());

  const shouldPromptLeaseStartDateAdjust =
    Boolean(requestData.lease_start_date) &&
    isBeforeToday(requestData.lease_start_date) &&
    !(applicationData?.rent_added || applicationAllData?.rent_added) &&
    requestData.rent_amount !== undefined &&
    !isApiOne(
      applicationData?.dont_update_lease_date ??
        applicationAllData?.dont_update_lease_date,
    );

  let shouldPromptLeaseDetailsChanged = false;
  if (
    isApprovedWithLeaseEmailStatus(applicationData) ||
    isApprovedWithLeaseEmailStatus(applicationAllData)
  ) {
    const oldPropertyId =
      applicationAllData?.property?.property_id ??
      applicationAllData?.fk_property_id;
    const nextPropertyId =
      formData.fk_property_id ??
      formData.property_id ??
      requestData.fk_property_id ??
      oldPropertyId;
    const propertyIdChanged =
      normalizeTextForCompare(nextPropertyId) !==
      normalizeTextForCompare(oldPropertyId);
    const leaseStartChanged =
      Boolean(
        propertyEffective.lease_start_date && applicationAllData?.lease_start_date,
      ) &&
      normalizeLeaseDateForCompare(propertyEffective.lease_start_date) !==
        normalizeLeaseDateForCompare(applicationAllData?.lease_start_date);
    const unitNumberChanged =
      normalizeTextForCompare(propertyEffective.unit_number) !==
      normalizeTextForCompare(applicationAllData?.unit_number);

    shouldPromptLeaseDetailsChanged =
      propertyIdChanged || leaseStartChanged || unitNumberChanged;
  }

  return {
    ok: true,
    requestData,
    shouldPromptLeaseStartDateAdjust,
    shouldPromptLeaseDetailsChanged,
  };
}

function buildIncomePayload(
  formData: ApplicationDetailFormDelta,
  applicationData: ApplicationLayerData,
): TabSaveBuildResult {
  const hasFormOtherSource = Object.prototype.hasOwnProperty.call(
    formData,
    "other_source",
  );
  const incomeEffective = buildIncomeEmploymentEffectiveData(
    formData,
    applicationData as Record<string, unknown>,
  );

  const incomeResult = validateIncomeEmploymentTab(incomeEffective);
  if (!incomeResult.isValid) {
    return {
      ok: false,
      kind: "validation",
      errors: incomeResult.errors,
      tab: "employmentinfo",
    };
  }

  const requestData: Record<string, unknown> = {};
  Object.assign(requestData, applicationTabValidation("income"));

  const isEmploymentRowForSave = (row: unknown): boolean => {
    if (!row || typeof row !== "object") return false;
    const r = row as Record<string, unknown>;
    return Boolean(
      String(r.employer_name ?? "").trim() ||
        String(r.employer_street ?? "").trim() ||
        String(r.employer_street_address ?? "").trim() ||
        String(r.employer_city ?? "").trim(),
    );
  };

  requestData.enter_date = (formData.enter_date as Record<string, string>) ?? {};
  requestData.employment_status = incomeEffective.employment_status ?? "1";
  requestData.ninja_reason =
    formData.ninja_reason ?? applicationData?.ninja_reason ?? "";

  const currentList = (incomeEffective.current_employment ?? []).filter(
    isEmploymentRowForSave,
  );
  const previousList = (incomeEffective.previous_employment ?? []).filter(
    isEmploymentRowForSave,
  );
  const otherList = (incomeEffective.other_source ?? []).filter(
    (row) =>
      row &&
      (String(row.source_name ?? "").trim() ||
        String(row.income ?? "").trim() ||
        String(row.job_start_date ?? "").trim()),
  );

  requestData.previous_employer = previousList.length > 0 ? "1" : "0";
  requestData.current_employment = currentList;
  requestData.previous_employment = previousList;
  if (hasFormOtherSource || otherList.length > 0) {
    requestData.other_source = otherList;
  }

  const deletedJobIds = extractDeletedJobIds(formData.deleted);
  if (deletedJobIds.length > 0) {
    requestData.deleted = { resident_job: deletedJobIds };
  }

  const verifiedOther =
    formData.underwriter_verified_income_other ??
    applicationData?.underwriter_verified_income_other;
  const verifiedType =
    formData.underwriter_verified_income_type ??
    applicationData?.underwriter_verified_income_type;
  if (verifiedOther != null && verifiedOther !== "") {
    requestData.underwriter_verified_income_other = String(verifiedOther);
  }
  if (verifiedType != null && verifiedType !== "") {
    requestData.underwriter_verified_income_type = String(verifiedType);
  }

  return {
    ok: true,
    requestData,
    shouldPromptLeaseStartDateAdjust: false,
    shouldPromptLeaseDetailsChanged: false,
  };
}

export function buildApplicationTabSavePayload(
  context: TabSaveBuildContext,
): TabSaveBuildResult {
  const {
    selectedTab,
    formData,
    applicationData,
    applicationAllData,
    leaseChangeAction,
    submitWithoutLeaseDateUpdate,
    tabDirty,
  } = context;

  let buildResult: TabSaveBuildResult;

  switch (selectedTab) {
    case "aboutyourselfinfo":
      buildResult = buildAboutYourselfPayload(formData, applicationData);
      break;
    case "propertyinfo":
      buildResult = buildPropertyPayload(
        formData,
        applicationData,
        applicationAllData,
      );
      break;
    case "employmentinfo":
      buildResult = buildIncomePayload(formData, applicationData);
      break;
    default:
      return {
        ok: true,
        requestData: { ...formData },
        shouldPromptLeaseStartDateAdjust: false,
        shouldPromptLeaseDetailsChanged: false,
      };
  }

  if (!buildResult.ok) {
    return buildResult;
  }

  let { requestData } = buildResult;
  const {
    shouldPromptLeaseStartDateAdjust,
    shouldPromptLeaseDetailsChanged,
  } = buildResult;

  if (selectedTab === "propertyinfo" && shouldPromptLeaseStartDateAdjust) {
    if (submitWithoutLeaseDateUpdate) {
      requestData = { ...requestData, dont_update_lease_date: "1" };
    } else {
      return { ok: false, kind: "lease_start_adjust" };
    }
  }

  if (
    (selectedTab === "propertyinfo" || selectedTab === "aboutyourselfinfo") &&
    !leaseChangeAction &&
    shouldPromptLeaseDetailsChanged
  ) {
    if (
      hasReimbursementRentalApplicationStatus(applicationData, applicationAllData)
    ) {
      return { ok: false, kind: "reimbursement_confirm" };
    }
    return { ok: false, kind: "lease_details_changed" };
  }

  if (leaseChangeAction === "email_property") {
    requestData = { ...requestData, resend_lease_details_email: "1" };
  }

  requestData = { ...requestData, enter_date: requestData.enter_date ?? {} };
  if (
    tabDirty &&
    applicationData.applicant_status === "AWAITING"
  ) {
    requestData.data_changed = "1";
  }
  if (selectedTab === "propertyinfo") {
    requestData = orderPropertyPayloadLikeAngular(requestData);
  }

  return {
    ok: true,
    requestData,
    shouldPromptLeaseStartDateAdjust: false,
    shouldPromptLeaseDetailsChanged: false,
  };
}
